import { Transition } from "@headlessui/react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { Languages } from "lucide-react"

import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { cn } from "@/lib/utils"
import { settingsLanguagePath } from "@/routes"
import type { BreadcrumbItem, SharedData } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Language settings",
    href: settingsLanguagePath(),
  },
]

type RomanizationSystem = "rtgs" | "paiboon"

interface PreviewEntry {
  thai: string
  english: string
  rtgs: string
  paiboon: string
}

const PREVIEW_DATA: PreviewEntry[] = [
  {
    thai: "สวัสดี",
    english: "Hello",
    rtgs: "sawatdi",
    paiboon: "sà-wàt-dii",
  },
  {
    thai: "ขอบคุณ",
    english: "Thank you",
    rtgs: "khopkhun",
    paiboon: "kɔ̀ɔp-kun",
  },
  {
    thai: "สบายดีไหม",
    english: "How are you?",
    rtgs: "sabaidimai",
    paiboon: "sà-baai-dii-mǎi",
  },
]

const SYSTEM_INFO: Record<
  RomanizationSystem,
  { label: string; description: string }
> = {
  rtgs: {
    label: "RTGS",
    description:
      "Royal Thai General System — the official Thai government standard. No tone markers. Used in road signs, maps, and official documents.",
  },
  paiboon: {
    label: "Paiboon",
    description:
      "Paiboon+ system — includes tone markers and long vowel indicators. Popular with Thai language learners for its pronunciation accuracy.",
  },
}

export default function Language() {
  const { auth } = usePage<SharedData>().props
  const currentSystem =
    (auth.user.romanization_system as RomanizationSystem) || "rtgs"

  const { data, setData, patch, processing, recentlySuccessful } = useForm({
    user: { romanization_system: currentSystem },
  })

  const selectedSystem = data.user.romanization_system

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(settingsLanguagePath(), { preserveScroll: true })
  }

  const activeInfo = SYSTEM_INFO[selectedSystem]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Language settings"
            description="Manage how Thai is displayed across the app"
          />

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Romanization system selector */}
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium">Romanization System</h4>
                <p className="text-muted-foreground mt-0.5 text-sm">
                  How Thai script is written in Latin letters
                </p>
              </div>

              <ToggleGroup
                type="single"
                variant="outline"
                value={selectedSystem}
                onValueChange={(value) => {
                  if (value) setData("user", { romanization_system: value as RomanizationSystem })
                }}
                className="w-full"
              >
                {(
                  Object.entries(SYSTEM_INFO) as [
                    RomanizationSystem,
                    (typeof SYSTEM_INFO)[RomanizationSystem],
                  ][]
                ).map(([key, info]) => (
                  <ToggleGroupItem
                    key={key}
                    value={key}
                    className={cn(
                      "flex-1 px-4 py-2.5 text-sm font-medium transition-all",
                      selectedSystem === key &&
                        "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
                    )}
                  >
                    {info.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>

              {/* Active system description */}
              <p className="text-muted-foreground text-xs leading-relaxed">
                {activeInfo.description}
              </p>
            </div>

            {/* Preview */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Languages className="text-muted-foreground size-4" />
                <h4 className="text-sm font-medium">Preview</h4>
              </div>

              <div className="border-border bg-muted/30 divide-border divide-y rounded-lg border">
                {PREVIEW_DATA.map((entry) => (
                  <div key={entry.thai} className="px-4 py-3">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-base font-medium">
                        {entry.thai}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {entry.english}
                      </span>
                    </div>
                    <p className="text-primary mt-1 text-sm font-medium italic">
                      {entry[selectedSystem]}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Save */}
            <div className="flex items-center gap-4">
              <Button type="submit" disabled={processing}>
                Save
              </Button>

              <Transition
                show={recentlySuccessful}
                enter="transition ease-in-out"
                enterFrom="opacity-0"
                leave="transition ease-in-out"
                leaveTo="opacity-0"
              >
                <p className="text-sm text-neutral-600">Saved</p>
              </Transition>
            </div>
          </form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
